enum StorageKey {
  UpgradeRequested = 'upgrade_requested',
  AvsPlusUpgradeRequested = 'avs_plus_upgrade_requested',
  GetStartedBannerDismissed = 'get_started_banner_dismissed',
  PromotionBannerDismissed = 'promotion_banner_dismissed',
  PromotionBannerDismissCount = 'promotion_banner_dismiss_count',
  LastPromotionBannerDismiss = 'last_promotion_banner_dismiss',
  LastAvsPageVisit = 'last_avs_page_visit',
  PromotionBannerFirstSeen = 'promotion_banner_first_seen',
  ProTipDismissed = 'pro_tip_dismissed',
  GuideClosed = 'guide_closed',
}

interface BannerState {
  isDismissed: boolean;
  dismissCount: number;
  lastDismissedAt: number;
  lastAvsPageVisit: number;
  firstSeenAt: number;
}

const getStorageKey = (
  environment: string,
  key: StorageKey,
  appId?: string,
) => {
  const baseKey = `avs_${environment}_${key}`;
  return appId ? `${baseKey}_${appId}` : baseKey;
};

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

export const accountVerificationStorage = {
  isUpgradeRequested: (environment: string, appId: string): boolean => {
    return (
      localStorage.getItem(
        getStorageKey(environment, StorageKey.UpgradeRequested, appId),
      ) === 'true'
    );
  },

  setUpgradeRequested: (environment: string, appId: string): void => {
    localStorage.setItem(
      getStorageKey(environment, StorageKey.UpgradeRequested, appId),
      'true',
    );
  },

  isAvsPlusUpgradeRequested: (environment: string, appId: string): boolean => {
    return (
      localStorage.getItem(
        getStorageKey(environment, StorageKey.AvsPlusUpgradeRequested, appId),
      ) === 'true'
    );
  },

  setAvsPlusUpgradeRequested: (environment: string, appId: string): void => {
    localStorage.setItem(
      getStorageKey(environment, StorageKey.AvsPlusUpgradeRequested, appId),
      'true',
    );
  },

  isBannerDismissed: (environment: string, appId: string): boolean => {
    return (
      localStorage.getItem(
        getStorageKey(environment, StorageKey.GetStartedBannerDismissed, appId),
      ) === 'true'
    );
  },

  setBannerDismissed: (environment: string, appId: string): void => {
    localStorage.setItem(
      getStorageKey(environment, StorageKey.GetStartedBannerDismissed, appId),
      'true',
    );
  },

  getBannerState: (environment: string, appId: string): BannerState => {
    const isDismissed =
      localStorage.getItem(
        getStorageKey(environment, StorageKey.PromotionBannerDismissed, appId),
      ) === 'true';

    const dismissCount = parseInt(
      localStorage.getItem(
        getStorageKey(
          environment,
          StorageKey.PromotionBannerDismissCount,
          appId,
        ),
      ) ?? '0',
      10,
    );

    const lastDismissedAt = parseInt(
      localStorage.getItem(
        getStorageKey(
          environment,
          StorageKey.LastPromotionBannerDismiss,
          appId,
        ),
      ) ?? '0',
      10,
    );

    const lastAvsPageVisit = parseInt(
      localStorage.getItem(
        getStorageKey(environment, StorageKey.LastAvsPageVisit, appId),
      ) ?? '0',
      10,
    );

    const firstSeenAt = parseInt(
      localStorage.getItem(
        getStorageKey(environment, StorageKey.PromotionBannerFirstSeen, appId),
      ) ?? '0',
      10,
    );

    return {
      isDismissed,
      dismissCount,
      lastDismissedAt,
      lastAvsPageVisit,
      firstSeenAt,
    };
  },

  dismissBanner: (environment: string, appId: string): void => {
    const state = accountVerificationStorage.getBannerState(environment, appId);

    localStorage.setItem(
      getStorageKey(environment, StorageKey.PromotionBannerDismissed, appId),
      'true',
    );

    localStorage.setItem(
      getStorageKey(environment, StorageKey.PromotionBannerDismissCount, appId),
      (state.dismissCount + 1).toString(),
    );

    localStorage.setItem(
      getStorageKey(environment, StorageKey.LastPromotionBannerDismiss, appId),
      Date.now().toString(),
    );
  },

  recordAvsPageVisit: (environment: string, appId: string): void => {
    localStorage.setItem(
      getStorageKey(environment, StorageKey.LastAvsPageVisit, appId),
      Date.now().toString(),
    );
  },

  initializeBannerFirstSeen: (environment: string, appId: string): void => {
    const state = accountVerificationStorage.getBannerState(environment, appId);
    if (!state.firstSeenAt) {
      localStorage.setItem(
        getStorageKey(environment, StorageKey.PromotionBannerFirstSeen, appId),
        Date.now().toString(),
      );
    }
  },

  shouldShowBanner: (environment: string, appId: string): boolean => {
    const state = accountVerificationStorage.getBannerState(environment, appId);
    const now = Date.now();

    if (state.dismissCount >= 2) return false;

    if (state.firstSeenAt && now - state.firstSeenAt > ONE_MONTH_MS)
      return false;

    if (state.lastDismissedAt && now - state.lastDismissedAt > ONE_MONTH_MS)
      return false;

    if (!state.isDismissed) return true;

    if (state.lastAvsPageVisit && now - state.lastAvsPageVisit < ONE_MONTH_MS) {
      return false;
    }

    return (
      state.lastDismissedAt != null &&
      now - state.lastDismissedAt > FIVE_DAYS_MS
    );
  },

  shouldShowNewNavigationItem: (
    environment: string,
    appId: string,
  ): boolean => {
    const state = accountVerificationStorage.getBannerState(environment, appId);
    const now = Date.now();
    return !(
      state.lastDismissedAt && now - state.lastDismissedAt > ONE_MONTH_MS
    );
  },

  isProTipDismissed: (environment: string, appId: string): boolean => {
    return (
      localStorage.getItem(
        getStorageKey(environment, StorageKey.ProTipDismissed, appId),
      ) === 'true'
    );
  },

  setProTipDismissed: (environment: string, appId: string): void => {
    localStorage.setItem(
      getStorageKey(environment, StorageKey.ProTipDismissed, appId),
      'true',
    );
  },

  isGuideClosed: (environment: string, appId?: string): boolean => {
    return (
      localStorage.getItem(
        getStorageKey(environment, StorageKey.GuideClosed, appId),
      ) === 'true'
    );
  },

  setGuideClosed: (environment: string, appId?: string, closed: boolean = true): void => {
    if (closed) {
      localStorage.setItem(
        getStorageKey(environment, StorageKey.GuideClosed, appId),
        'true',
      );
    } else {
      localStorage.removeItem(
        getStorageKey(environment, StorageKey.GuideClosed, appId),
      );
    }
  },
};
